import type { WasteStatsBucket } from "@/hooks/use-waste-stats";
import type { Lens } from "@/hooks/use-lens";
import type { TFunction } from "@/i18n/catalog";
import type { Locale } from "@/i18n/config";
import { heatAlpha, heatmapMatrix, type HeatmapCell } from "@/lib/heatmap";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency, formatWeight, weekdayLabels } from "@/lib/format";
import { ChartCard } from "@/components/waste/reports/chart-card";
import { cn } from "@/lib/utils";

/** Literal keys in a lookup table — a key built at runtime is invisible to `check-i18n.mjs`. */
const SERVICE_KEYS: Record<string, string> = {
  breakfast: "reference.serviceTime.breakfast",
  lunch: "reference.serviceTime.lunch",
  dinner: "reference.serviceTime.dinner",
  unassigned: "reference.serviceTime.unassigned",
};

export type HeatmapDrill = HeatmapCell & { label: string };

type Props = {
  /** "Explore this" target for this panel (POS2-886); null when unavailable. */
  exploreHref?: string | null;
  buckets: WasteStatsBucket[];
  lens: Lens;
  currency: string;
  /** Hot-cell click/Enter — filters The Proof to that service + day (POS2-873). */
  onDrill: (cell: HeatmapDrill) => void;
};

/** "When it happens" (design §8): services × site-local days of week, blue
 * intensity scaled to the visible max. The hottest cell is outlined and drills
 * into The Proof — the outline is the annotation, no explanatory sentence. */
export function WhenHeatmap({ exploreHref, buckets, lens, currency, onDrill }: Props) {
  const { locale, t } = useTranslation();
  const dayLabels = weekdayLabels(locale);
  const { rows, max, hot } = heatmapMatrix(buckets, lens);

  return (
    <ChartCard
      title={t("dashboard.widget.when")}
      description={t("dashboard.whenDescription")}
      isEmpty={max === 0}
      exploreHref={exploreHref}
    >
      {/* The grid scrolls rather than fits (POS2-1072): its column widths are set by the
          figures in the cells, so at 390px — 260px of card — the seven days need 307px and
          no amount of tightening survives a site with bigger numbers. The service column
          stays put while the days scroll, so a scrolled row is still identifiable.
          The horizontal gap is cell padding rather than border-spacing, so the sticky
          column's background meets the next cell and scrolled cells cannot show through
          the gutter; px-1 keeps the hot cell's offset outline off the clip edge. */}
      <div className="-mx-1 overflow-x-auto px-1">
        <table
          className="w-full border-separate border-spacing-x-0 border-spacing-y-[5px]"
          data-testid="when-heatmap"
        >
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-white" />
              {dayLabels.map((day) => (
                <th
                  key={day}
                  className="px-[2.5px] pb-1 text-center text-xs font-medium text-gray-500"
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.service}>
                <td className="sticky left-0 z-10 whitespace-nowrap bg-white pr-2 text-left text-xs font-medium text-gray-500">
                  {serviceLabel(t, row.service)}
                </td>
                {row.values.map((value, i) => {
                  const isHot = hot?.service === row.service && hot.isoDow === i + 1;
                  const style = {
                    background: `rgba(37,99,235,${heatAlpha(value, max).toFixed(3)})`,
                  };
                  const text = formatCell(locale, value, lens, currency);
                  return (
                    <td key={i} className="px-[2.5px] py-0 text-center">
                      {isHot ? (
                        <button
                          type="button"
                          onClick={() =>
                            onDrill({
                              service: row.service,
                              isoDow: i + 1,
                              label: `${serviceLabel(t, row.service)} · ${dayLabels[i]}`,
                            })
                          }
                          title={t("dashboard.whenCellTitle")}
                          style={style}
                          className={cn(
                            "w-full rounded-[7px] py-[11px] text-xs font-semibold tabular-nums text-gray-900",
                            "outline outline-2 outline-offset-1 outline-red-500 transition-[outline-width] hover:outline-4",
                          )}
                          data-testid="heatmap-hot-cell"
                        >
                          {text}
                        </button>
                      ) : (
                        <div
                          style={style}
                          className="rounded-[7px] py-[11px] text-xs font-semibold tabular-nums text-gray-900"
                        >
                          {text}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
}

/** Compact cell value in the current lens — whole units read best at 21 cells. */
function serviceLabel(t: TFunction, service: string): string {
  return SERVICE_KEYS[service] ? t(SERVICE_KEYS[service]) : service;
}

function formatCell(locale: Locale, value: number, lens: Lens, currency: string): string {
  if (lens === "weight") return formatWeight(locale, value, value >= 10 ? 0 : 1);
  return formatCurrency(locale, value, currency, 0);
}
